import { useApolloClient } from '@apollo/client';
import {
  BookingFragmentDoc,
  BookingUpdateFragmentDoc,
  useOnBookingUpdateSubscription,
  useOnBookingCreateSubscription,
} from '@graphql';
import { useSession, useToastError } from '@hooks';
import { toast } from 'react-toastify';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

export function useBookingSubscription() {
  const { account } = useSession();
  const toastError = useToastError();
  const client = useApolloClient();
  const { t } = useTranslation('booking');

  useOnBookingCreateSubscription({
    skip: !account,
    onError: toastError,
    onData: ({ data }) => {
      const newBooking = data.data?.bookingCreate;
      if (!newBooking) return;

      const bookingRef = client.cache.writeFragment({
        fragment: BookingFragmentDoc,
        data: { ...newBooking, __typename: 'Booking' },
      });

      client.cache.modify({
        fields: {
          myBookings(existingRefs = [], { readField }) {
            const exists = existingRefs.some((ref: any) => readField('id', ref) === newBooking.id);
            if (exists) return existingRefs;
            return [bookingRef, ...existingRefs];
          },
        },
      });

      if (account && account.id !== newBooking.author.id) {
        toast.info(
          t('new.new booking for startTime', { startTime: moment(newBooking.startTime).format('LL') })
        );
      }
    },
  });

  useOnBookingUpdateSubscription({
    skip: !account,
    onError: toastError,
    onData: ({ data }) => {
      const update = data.data?.bookingUpdate;
      if (!update) return;

      // 1) Write/merge the Booking fields in cache (this updates any query lists that reference this booking)
      client.cache.writeFragment({
        fragment: BookingUpdateFragmentDoc,
        data: { ...update, __typename: 'BookingUpdate' },
      });

      // 2) Keep the Booking.updates list in sync (prepend update if missing)
      client.cache.modify({
        id: client.cache.identify({ __typename: 'Booking', id: update.booking.id }),
        fields: {
          updates(existingRefs = [], { readField }) {
            const newUpdateRef = client.cache.writeFragment({
              data: { ...update, __typename: 'BookingUpdate' },
              fragment: BookingUpdateFragmentDoc,
            });

            const exists = existingRefs.some((ref: any) => readField('id', ref) === update.id);
            if (exists) return existingRefs;

            return [newUpdateRef, ...existingRefs];
          },
        },
      });

      // toast notification for booking updates from others for changes but not new message
      if (account && account.id !== update.author.id && update.message === null) {
        const author =
          update.author.__typename === 'Consumer'
            ? `${update.author.user.firstname} ${update.author.user.lastname}.`
            : update.author.name;
        const startTime = moment(update.booking.startTime).format('LL');
        toast.info(t('update.author updated booking of startTime', { author, startTime }));
      }
    },
  });

  return null;
}
